import { useCallback, useRef, useState } from "react";

import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";
import { ClientInfo } from "~/pages/purchase-orders/hooks/utils/clientUtils";

// Type for client data extracted from initial query
export interface ClientData {
  names: Record<string, string>;
  info: Record<string, ClientInfo>;
  riskStatus: Record<string, boolean>;
  salesConditions: Record<string, string>;
}

// Type for the return value that includes both formatted requests and client data
export interface FetchResult {
  formattedRequests: SalesRequest[];
  clientData: ClientData;
}

const PAGE_SIZE = 500; // Number of records per page - reduced for better performance

// Helper function to fetch previous status for rejected sales notes
const fetchPreviousStatusForRejected = async (
  salesRequests: SalesRequest[]
) => {
  const rejectedRequests = salesRequests.filter(
    (req) => req.status === "rejected"
  );

  if (rejectedRequests.length === 0) return;

  try {
    // Get the previous status for each rejected request
    const rejectedIds = rejectedRequests.map((req) => req.id);

    const { data: statusHistory, error } = await supabase
      .from("sales_request_status_history")
      .select("sales_request_id, old_status")
      .in("sales_request_id", rejectedIds)
      .eq("new_status", "rejected")
      .order("changed_at", { ascending: false });

    if (error) {
      console.error(
        "Error fetching previous status for rejected sales notes:",
        error
      );
      return;
    }

    if (statusHistory) {
      // Create a map of sales_request_id to previous status
      const previousStatusMap: Record<string, string> = {};
      statusHistory.forEach((entry) => {
        // Use the first (most recent) entry for each sales request
        if (!previousStatusMap[entry.sales_request_id] && entry.old_status) {
          previousStatusMap[entry.sales_request_id] = entry.old_status;
        }
      });

      // Update the sales requests with previous status
      rejectedRequests.forEach((req) => {
        req.previousStatus = previousStatusMap[req.id];
      });
    }
  } catch (error) {
    console.error(
      "Error fetching previous status for rejected sales notes:",
      error
    );
  }
};

export const useFetchSalesRequests = (
  setSalesRequests: React.Dispatch<React.SetStateAction<SalesRequest[]>>,
  viewAllSellers: boolean = false
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  // Use ref to track page value without triggering useCallback recreation
  const pageRef = useRef(page);
  pageRef.current = page;

  // Get current user profile to restrict access based on role
  // Use selectors to avoid rerendering when unrelated store properties change
  const currentUserId = useProfileStore((state) => state.id);
  const userRole = useProfileStore((state) => state.role);

  const fetchSalesRequests = useCallback(async (loadMore: boolean = false, isBackgroundRefresh: boolean = false) => {
    if (loadMore) {
      setIsLoadingMore(true);
    } else if (!isBackgroundRefresh) {
      // Only set loading state for initial load, not for background realtime refreshes
      setIsLoading(true);
      setPage(0);
      setHasMore(true);
    }

    try {
      // Use ref to avoid page being in dependency array (causes infinite loop when setPage is called)
      const currentPage = loadMore ? pageRef.current : 0;
      const offset = currentPage * PAGE_SIZE;

      // Build the base query with role-based filtering
      // Note: payment_count is now fetched separately to avoid expensive subqueries
      // Client data is fetched in this query to avoid N+1 queries
      let query = supabase
        .from("sales_request")
        .select(
          `
          *,
          assigned_transportista,
          paid_amount,
          clients:client_id(
            id,
            name,
            deleted_at,
            tax_id,
            risk_checked,
            sales_condition_id,
            fiscal_address_id,
            addresses:fiscal_address_id(street, number, city, comuna, state)
          ),
          profiles:created_by(id, name, lastname, email, role),
          factura_documents:sales_request_documents!sales_request_id(bsale_number, document_type)
        `,
          { count: "exact" }
        )
        .is("deleted_at", null) // Exclude soft-deleted records
        .range(offset, offset + PAGE_SIZE - 1); // Add pagination

      // Apply role-based filtering
      if (userRole === "seller" && currentUserId && !viewAllSellers) {
        query = query.eq("created_by", currentUserId);
      } else if (userRole === "cobranza" && !viewAllSellers) {
        // Cobranza role sees orders they need to process in the new workflow:
        // - pending_pricing: For pricing approvals (sales team can also approve these)
        // - pending: Orders waiting for cobranza approval
        // - prepaid: Orders with prepaid condition needing payment registration
        // - payment_to_check: Orders with payments to validate
        // - accepted: All approved orders (both paid and unpaid) for tracking
        query = query.or(
          "status.eq.pending_pricing,status.eq.pending,status.eq.prepaid,status.eq.payment_to_check,status.eq.accepted"
        );
      } else if (userRole === "transportista") {
        // Transportista role sees orders assigned to them with "in_transit", "delivered", and "rejected" statuses
        // They need to see rejected orders to understand why they were rejected
        query = query
          .eq("assigned_transportista", currentUserId)
          .in("status", ["in_transit", "delivered", "rejected"]);
      }
      // All other roles (including superadmin) see all sales requests by default

      // Execute the query with appropriate ordering
      const {
        data: salesRequestsData,
        error: salesRequestsError,
        count,
      } = await query.order("created_at", { ascending: false });

      if (salesRequestsError) throw salesRequestsError;

      if (salesRequestsData) {
        // Convert to our frontend interface format
        const formattedRequests: SalesRequest[] = salesRequestsData.map(
          (req) => {
            const result = {
              id: req.id,
              clientId: req.client_id,
              createdBy: req.created_by,
              approvedRejectedBy: req.approved_rejected_by || undefined,
              status: req.status as
                | "pending"
                | "pending_pricing"
                | "pending_credit"
                | "prepaid"
                | "accepted"
                | "por_facturar"
                | "rejected"
                | "in_transit"
                | "delivered"
                | "payment_to_check"
                | "test",
              paid: req.paid,
              paid_amount: req.paid_amount || 0,
              payment_count: 0, // Will be fetched separately for better performance
              paymentTerms: req.payment_terms,
              paymentType: req.payment_type as "future" | "prepaid",
              paymentDueDate: req.payment_due_date,
              totalAmount: req.total_amount,
              isExento: req.is_exento || undefined,
              isNonSale: req.is_non_sale || undefined,
              hasPricingWarning: req.has_pricing_warning || false,
              comments: req.comments || undefined,
              internal_notes: req.internal_notes || undefined,
              factura_observations: req.factura_observations || undefined,
              deliveryDate: req.delivery_date || undefined,
              deliveryAddressId: req.delivery_address_id || undefined,
              // Map the address fields
              deliveryStreet: req.delivery_street || undefined,
              deliveryNumber: req.delivery_number || undefined,
              deliveryComplement: req.delivery_complement || undefined,
              deliveryCity: req.delivery_city || undefined,
              deliveryState: req.delivery_state || undefined,
              deliveryComuna: req.delivery_comuna || undefined,
              rejectedReason: req.rejected_reason || undefined,
              createdAt: req.created_at,
              updatedAt: req.updated_at,
              salesConditionId: req.sales_condition_id || undefined,
              assigned_transportista: req.assigned_transportista || undefined,
              invoice_printed_at: req.invoice_printed_at || undefined,
              factura_number: (() => {
                // Extract factura number from joined documents
                const facturaDoc = req.factura_documents?.find(
                  (doc: any) =>
                    doc.document_type === "factura" && doc.bsale_number
                );
                return facturaDoc?.bsale_number || undefined;
              })(),
            };

            return result;
          }
        );

        // Fetch previous status for rejected sales notes
        await fetchPreviousStatusForRejected(formattedRequests);

        // Build client data maps from the already-fetched client data (eliminates N+1 queries)
        const clientNamesMap: Record<string, string> = {};
        const clientInfoMap: Record<string, ClientInfo> = {};
        const clientRiskStatusMap: Record<string, boolean> = {};
        const clientSalesConditionsMap: Record<string, string> = {};
        const salesConditionIdsToFetch: Set<string> = new Set();

        salesRequestsData.forEach((req) => {
          const client = req.clients as any;
          if (client && !clientNamesMap[client.id]) {
            // Client names
            clientNamesMap[client.id] = client.name || "Cliente sin nombre";

            // Client risk status
            clientRiskStatusMap[client.id] = client.risk_checked || false;

            // Client sales conditions (store ID for now, will fetch names later)
            if (client.sales_condition_id) {
              clientSalesConditionsMap[client.id] = client.sales_condition_id;
              salesConditionIdsToFetch.add(client.sales_condition_id);
            }

            // Get fiscal address data from the joined addresses table
            const fiscalAddress =
              client.addresses && typeof client.addresses === "object"
                ? (client.addresses as any)
                : null;

            // Client info (will be enriched with sales_condition details)
            clientInfoMap[client.id] = {
              name: client.name || "Cliente sin nombre",
              isDeleted: !!client.deleted_at,
              taxId: client.tax_id || undefined,
              fiscalAddress: fiscalAddress
                ? {
                    street: fiscalAddress.street || "",
                    number: fiscalAddress.number || "",
                    city: fiscalAddress.city || "",
                    comuna: fiscalAddress.comuna || undefined,
                    state: fiscalAddress.state || undefined,
                  }
                : undefined,
            };
          }
        });

        // Fetch sales conditions in a single query and enrich client info
        if (salesConditionIdsToFetch.size > 0) {
          const { data: salesConditionsData } = await supabase
            .from("sales_conditions")
            .select("id, name, type")
            .in("id", Array.from(salesConditionIdsToFetch));

          if (salesConditionsData) {
            const conditionsMap = new Map(
              salesConditionsData.map((sc) => [sc.id, sc])
            );

            // Enrich client info with sales condition details
            Object.keys(clientInfoMap).forEach((clientId) => {
              const salesConditionId = clientSalesConditionsMap[clientId];
              if (salesConditionId) {
                const condition = conditionsMap.get(salesConditionId);
                if (condition) {
                  clientInfoMap[clientId].salesConditionName = condition.name;
                  clientInfoMap[clientId].salesConditionType = condition.type;
                }
              }
            });
          }
        }

        const clientData: ClientData = {
          names: clientNamesMap,
          info: clientInfoMap,
          riskStatus: clientRiskStatusMap,
          salesConditions: clientSalesConditionsMap,
        };

        // Update state based on whether we're loading more or starting fresh
        if (loadMore) {
          setSalesRequests((prev) => [...prev, ...formattedRequests]);
        } else {
          setSalesRequests(formattedRequests);
        }

        // Check if there are more records to load
        const totalFetched = offset + formattedRequests.length;
        setHasMore(
          count ? totalFetched < count : formattedRequests.length === PAGE_SIZE
        );

        if (loadMore) {
          setPage(currentPage + 1);
        } else {
          setPage(1);
        }

        return { formattedRequests, clientData };
      }
      return null;
    } catch (error: unknown) {
      console.error("Error fetching sales requests:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to fetch sales requests",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [currentUserId, userRole, viewAllSellers, setSalesRequests]);

  const loadMoreSalesRequests = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      fetchSalesRequests(true);
    }
  }, [isLoadingMore, hasMore, fetchSalesRequests]);

  return {
    isLoading,
    isLoadingMore,
    hasMore,
    fetchSalesRequests,
    loadMoreSalesRequests,
  };
};
